import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api-client';
import { logger } from '@/lib/default-logger';
import { toast } from '@/components/core/toaster';

export interface Plugin {
  id: string;
  title: string;
  enabled: boolean;
  plugin: {
    name: string;
    title: string;
  };
  settings: Record<string, any>;
}

export interface PluginsResponse {
  data: Plugin[];
  total: number;
}

export interface PluginFormData {
  title: string;
  enabled: boolean;
  plugin: string;
  settings: Record<string, any>;
}

export interface PluginType {
  name: string;
  title: string;
}

export interface PluginTypeSettings {
  fields: Array<{
    name: string;
    label: string;
    type: string;
    required?: boolean;
    options?: any[];
  }>;
}

export const pluginsKeys = {
  all: ['plugins'] as const,
  lists: () => [...pluginsKeys.all, 'list'] as const,
  list: (query?: string) => [...pluginsKeys.lists(), { query }] as const,
  details: () => [...pluginsKeys.all, 'detail'] as const,
  detail: (id: string) => [...pluginsKeys.details(), id] as const,
  types: () => [...pluginsKeys.all, 'types'] as const,
  typeSettings: (type: string) => [...pluginsKeys.all, 'type-settings', type] as const,
  services: (type: string) => [...pluginsKeys.all, 'services', type] as const,
  serviceFields: (type: string, plugin: string) => [...pluginsKeys.all, 'service-fields', type, plugin] as const,
};

export function usePluginsList(query?: string) {
  return useQuery({
    queryKey: pluginsKeys.list(query),
    queryFn: async (): Promise<PluginsResponse> => {
      const queryString = query ? `?${query}` : '';
      const response = await apiClient.get(`/plugins${queryString}`);
      return response.data;
    },
  });
}

export function usePlugin(id: string) {
  return useQuery({
    queryKey: pluginsKeys.detail(id),
    queryFn: async (): Promise<Plugin> => {
      const response = await apiClient.get(`/plugins/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function usePluginTypes() {
  return useQuery({
    queryKey: pluginsKeys.types(),
    queryFn: async (): Promise<PluginType[]> => {
      const response = await apiClient.get('/plugin-types');
      return response.data;
    },
  });
}

export function usePluginTypeSettings(pluginType: string) {
  return useQuery({
    queryKey: pluginsKeys.typeSettings(pluginType),
    queryFn: async (): Promise<PluginTypeSettings> => {
      const response = await apiClient.get(`/plugin-types/${pluginType}`);
      return response.data;
    },
    enabled: !!pluginType,
  });
}

export function useCreatePlugin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: PluginFormData): Promise<Plugin> => {
      const response = await apiClient.post('/plugins', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pluginsKeys.lists() });
      toast.success('Plugin created successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}

export function useUpdatePlugin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PluginFormData }): Promise<Plugin> => {
      const response = await apiClient.put(`/plugins/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(pluginsKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: pluginsKeys.lists() });
      toast.success('Plugin updated successfully');
    },
    onError: (error: any) => {
      logger.error(error);
      const errorMessage = error?.response?.data?.message;
      toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || 'Something went wrong!');
    },
  });
}

export function useDeletePlugin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/plugins/${id}`);
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: pluginsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: pluginsKeys.lists() });
      toast.success("Plugin deleted successfully");
    },
    onError: (error: any) => {
      logger.error(error);
      toast.error("Something went wrong!");
    },
  });
}

export function usePluginServices(type: string) {
  return useQuery({
    queryKey: pluginsKeys.services(type),
    queryFn: async (): Promise<any[]> => {
      const response = await apiClient.get(`/plugin-services/${type}`);
      return response.data;
    },
    enabled: !!type,
  });
}

export function usePluginServiceFields(type: string, plugin: string) {
  return useQuery({
    queryKey: pluginsKeys.serviceFields(type, plugin),
    queryFn: async (): Promise<any> => {
      const response = await apiClient.get(`/plugin-services/${type}/${plugin}`);
      return response.data;
    },
    enabled: !!type && !!plugin,
  });
}